import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { generateColorPalette, hasGoodContrast, getReadableTextColor } from '../../utils/colorUtils';
import ProfilePreview from '../ProfilePreview';
import { COLOR_THEMES } from '../../constants/themes';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';

const AE_CSS = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .ae-title{font-family:'abcNormal','Inter','Arial',sans-serif;font-weight:300;letter-spacing:-0.01em;color:#0A0A0A;font-size:28px;margin:0;}
  .ae-subtitle{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:#6B6B6B;margin-top:4px;letter-spacing:-0.01em;}
  .ae-section{background:#fff;border:1px solid #E5E5E5;border-radius:12px;padding:24px;}
  .ae-sec-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;margin:0 0 16px;}
  .ae-banner{padding:10px 16px;border-radius:10px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;letter-spacing:-0.01em;border:1px solid;}
  .ae-banner--ok{background:rgba(52,199,89,0.08);border-color:rgba(52,199,89,0.2);color:#1a7f3c;}
  .ae-banner--error{background:rgba(255,59,48,0.08);border-color:rgba(255,59,48,0.2);color:#ff3b30;}
  .ae-banner--warn{background:rgba(255,204,0,0.12);border-color:rgba(255,204,0,0.35);color:#8a6d00;}
  .ae-form-label{display:block;font-size:12px;font-weight:500;color:#6B6B6B;margin-bottom:6px;letter-spacing:-0.01em;font-family:'Inter',system-ui,sans-serif;}
  .ae-color-input{width:44px;height:44px;border-radius:10px;border:1px solid #E5E5E5;cursor:pointer;padding:2px;flex-shrink:0;}
  .ae-text-input{flex:1;border:1px solid rgba(0,0,0,0.12);border-radius:9px;padding:9px 12px;font-size:14px;color:#0A0A0A;font-family:'Inter',system-ui,sans-serif;letter-spacing:-0.01em;background:#fff;outline:none;transition:border-color .15s,box-shadow .15s;box-sizing:border-box;}
  .ae-text-input:focus{border-color:#0071e3;box-shadow:0 0 0 3px rgba(0,113,227,0.12);}

  .ae-savebar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 16px;border-radius:10px;border:1px solid #E5E5E5;background:#FAFAFA;font-family:'Inter',system-ui,sans-serif;transition:background .15s,border-color .15s;}
  .ae-savebar.is-dirty{border-color:rgba(0,113,227,0.35);background:rgba(0,113,227,0.06);}
  .ae-savebar-label{font-size:13px;color:#8A8A8E;font-weight:500;letter-spacing:-0.01em;}
  .ae-savebar.is-dirty .ae-savebar-label{color:#0071e3;}
  .ae-discard-btn{background:none;border:none;font-size:13px;font-weight:600;color:#6B6B6B;cursor:pointer;padding:8px 10px;font-family:'Inter',system-ui,sans-serif;}
  .ae-discard-btn:hover{color:#0A0A0A;}
  .ae-discard-btn:disabled{opacity:.5;cursor:not-allowed;}
  .ae-save-btn--bar{width:auto;padding:9px 18px;font-size:13px;}

  .ae-tpl-grid{display:flex;gap:10px;}
  .ae-tpl-btn{flex:1;text-align:left;padding:14px 16px;border:2px solid #E5E5E5;border-radius:10px;background:#fff;cursor:pointer;transition:border-color .15s,background .15s;display:flex;flex-direction:column;gap:4px;}
  .ae-tpl-btn:hover{border-color:#D4D4D4;}
  .ae-tpl-btn.on{border-color:#0A0A0A;background:#0A0A0A;}
  .ae-tpl-name{font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;}
  .ae-tpl-btn.on .ae-tpl-name{color:#fff;}
  .ae-tpl-desc{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:#8A8A8E;letter-spacing:-0.01em;line-height:1.35;}
  .ae-tpl-btn.on .ae-tpl-desc{color:rgba(255,255,255,0.65);}

  .ae-theme-card{cursor:pointer;border:2px solid #E5E5E5;border-radius:10px;padding:16px;transition:border-color .15s,box-shadow .15s;}
  .ae-theme-card:hover{border-color:#D4D4D4;}
  .ae-theme-card.on{border-color:#0A0A0A;box-shadow:0 0 0 1px #0A0A0A;}
  .ae-theme-card--custom{border-style:dashed;}
  .ae-theme-card--custom.on{border-style:solid;}
  .ae-custom-colors{margin-top:20px;padding-top:20px;border-top:1px solid #E5E5E5;}
  .ae-tpl-note{background:#FAFAFA;border:1px solid #E5E5E5;border-radius:10px;padding:12px 14px;font-size:12.5px;color:#6B6B6B;line-height:1.5;font-family:'Inter',system-ui,sans-serif;letter-spacing:-0.01em;margin:0;}

  .ae-palette-box{background:#FAFAFA;border:1px solid #E5E5E5;border-radius:10px;padding:16px;}
  .ae-apply-btn{font-size:12px;font-weight:600;background:#0A0A0A;color:#fff;padding:5px 14px;border-radius:8px;border:none;cursor:pointer;transition:background .15s;font-family:'Inter',system-ui,sans-serif;}
  .ae-apply-btn:hover{background:#1a1a1a;}
  .ae-save-btn{width:100%;background:#0A0A0A;color:#fff;border:none;border-radius:10px;padding:11px 18px;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s;font-family:'Inter',system-ui,sans-serif;}
  .ae-save-btn:hover{opacity:.86;}
  .ae-save-btn:disabled{opacity:.5;cursor:not-allowed;}

  .ae-wm-row{display:flex;align-items:center;gap:12px;}
  .ae-wm-check{width:36px;height:36px;border-radius:50%;background:rgba(22,163,74,0.12);color:#16A34A;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .ae-wm-active-title{font-size:13.5px;font-weight:600;color:#0A0A0A;font-family:'Inter',system-ui,sans-serif;letter-spacing:-0.01em;}
  .ae-wm-active-sub{font-size:12px;color:#6B6B6B;margin-top:2px;font-family:'Inter',system-ui,sans-serif;}
  .ae-wm-btn{width:100%;display:flex;align-items:center;justify-content:center;gap:8px;padding:11px 16px;border-radius:10px;border:1.5px solid #E5E5E5;background:#fff;color:#0A0A0A;font-size:13.5px;font-weight:500;font-family:'Inter',system-ui,sans-serif;transition:background .15s,border-color .15s;}
  .ae-wm-btn:hover{background:#FAFAFA;border-color:#D4D4D4;}
  .ae-wm-hint{font-size:11.5px;color:#9A9A9A;margin-top:8px;text-align:center;font-family:'Inter',system-ui,sans-serif;}

  /* Brand-kit inheritance banners (Enterprise) — sit above Plantilla/Color
     when this profile's look is tied to the account's shared brand kit. */
  .ae-brand-banner{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;background:#FAFAFA;border:1px solid #E5E5E5;border-radius:12px;padding:14px 16px;font-family:'Inter',system-ui,sans-serif;}
  .ae-brand-banner-text{font-size:13px;color:#4B4B4F;line-height:1.5;letter-spacing:-0.01em;}
  .ae-brand-banner-text strong{color:#0A0A0A;}
  .ae-brand-banner-btn{flex-shrink:0;background:#fff;border:1.5px solid #E5E5E5;border-radius:9px;padding:7px 14px;font-size:12.5px;font-weight:600;color:#0A0A0A;cursor:pointer;font-family:inherit;transition:border-color .15s,background .15s;white-space:nowrap;}
  .ae-brand-banner-btn:hover{border-color:#D4D4D4;background:#fff;}

  /* Free-plan lock — dims the control and shows a platform-styled tooltip
     on hover instead of hiding the feature outright. Reused (without the
     upgrade-modal click-through) for brand-kit-inherited Plantilla/Color:
     those stay dimmed via this class, but are made inert in JS (guarded
     handlers below), not via pointer-events, so the hover tooltip keeps
     working on the section itself. */
  .ae-locked{opacity:.42;filter:grayscale(.15);}
  .ae-locked, .ae-locked *{cursor:not-allowed!important;}
  [data-locked-tip]{position:relative;}
  [data-locked-tip]::after{
    content:attr(data-locked-tip);
    position:absolute;bottom:calc(100% + 9px);left:50%;
    transform:translateX(-50%) translateY(4px);
    background:#1d1d1f;color:#fff;font-size:12px;font-weight:500;letter-spacing:-0.01em;
    padding:6px 11px;border-radius:7px;white-space:nowrap;pointer-events:none;
    opacity:0;visibility:hidden;transition:opacity .12s ease,transform .12s ease;transition-delay:.05s;
    z-index:40;box-shadow:0 4px 14px rgba(0,0,0,0.2);font-family:'Inter',system-ui,sans-serif;
  }
  [data-locked-tip]:hover::after{opacity:1;visibility:visible;transform:translateX(-50%) translateY(0);}

  @media (max-width: 768px) {
    .ae-tpl-btn{padding:10px 12px;}
    .ae-tpl-name{font-size:13px;}
    .ae-tpl-desc{font-size:11px;}
    .ae-theme-card{padding:11px;}
    .ae-palette-box{padding:11px;border-radius:7px;}
    .ae-apply-btn{font-size:10.5px;padding:4px 9px;min-height:34px;}
    .ae-save-btn{padding:8px 12px;font-size:11px;min-height:34px;}
    .ae-save-btn--bar{min-height:34px;}
    .ae-savebar{padding:11px 13px;flex-wrap:wrap;}
    .ae-savebar-label{font-size:12px;}
    .ae-preview-frame{width:100%!important;max-width:300px;margin:0 auto;}
    .ae-color-swatch-lg{width:40px!important;height:40px!important;}
    .ae-color-swatch-sm{width:10px!important;height:10px!important;}
    .ae-palette-swatch{height:38px!important;}
    .ae-section{padding:15px!important;}
    .ae-sec-title{font-size:13.5px!important;margin-bottom:12px!important;}
    .ae-mobile-controls{gap:14px!important;}
    .ae-mobile-preview-first{order:-1;position:static!important;}
    .ae-mobile-theme-grid{grid-template-columns:repeat(2,1fr)!important;}
    .ae-mobile-stack{gap:14px!important;}
  }
`;

// COLOR_THEMES (constants/themes.js) keeps its display names in Spanish only
// — translated here at render time instead of duplicating the theme data.
const themeName = (key, theme, t) => t(`theme.${key}`, theme.name);

const buildDraft = (profile) => ({
  template: profile?.template || 'classic',
  theme: profile?.theme_color || 'blue',
  background_color: profile?.background_color || '#ffffff',
  text_color: profile?.text_color || '#000000',
  button_color: profile?.button_color || '#7CDAF9',
});

const AppearanceEditor = ({ profile, updateProfile, setActiveTab }) => {
  const { hasFeature, openUpgrade } = usePlan();
  const { t } = useLanguage();
  const canRemoveWatermark = hasFeature('remove_watermark');
  const canUseBrandKit = hasFeature('brand_kit');
  const [draft, setDraft] = useState(() => buildDraft(profile));
  const [saved, setSaved] = useState(() => buildDraft(profile));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  // Whether the ACCOUNT has ever configured a brand kit at all — separate
  // from `heredaMarca` below (this profile's own opt-in/out of it). Only
  // fetched for Enterprise accounts; everyone else never sees these banners.
  const [brandKitExists, setBrandKitExists] = useState(false);
  const [applyingBrand, setApplyingBrand] = useState(false);

  const heredaMarca = !!profile?.hereda_marca;

  useEffect(() => {
    if (profile) {
      const next = buildDraft(profile);
      setDraft(next);
      setSaved(next);
    }
  }, [profile]);

  useEffect(() => {
    if (!canUseBrandKit) return;
    let mounted = true;
    axios.get(`${API}/teams/brand-kit`)
      .then((res) => { if (mounted) setBrandKitExists(!!res.data?.brand_kit); })
      .catch(() => { if (mounted) setBrandKitExists(false); });
    return () => { mounted = false; };
  }, [canUseBrandKit]);

  // "Personalizar solo este perfil": freezes whatever is showing right now
  // (the brand kit's resolved look, since the profile fetch already applied
  // it) into this profile's own fields, then stops inheriting. Reuses the
  // normal save path, no separate endpoint needed.
  const handleCustomizeThisProfile = async () => {
    setApplyingBrand(true);
    const result = await updateProfile({
      hereda_marca: false,
      template: draft.template,
      theme_color: draft.theme,
      background_color: draft.background_color,
      text_color: draft.text_color,
      button_color: draft.button_color,
    });
    if (!result.success) setMessage(`Error: ${result.error}`);
    setApplyingBrand(false);
  };

  // "Usar la marca de mi empresa": just flips the flag — colors/template are
  // resolved live from the account's brand kit from then on, nothing to send.
  const handleUseCompanyBrand = async () => {
    setApplyingBrand(true);
    const result = await updateProfile({ hereda_marca: true });
    if (!result.success) setMessage(`Error: ${result.error}`);
    setApplyingBrand(false);
  };

  const isDirty =
    draft.template !== saved.template ||
    draft.theme !== saved.theme ||
    draft.background_color !== saved.background_color ||
    draft.text_color !== saved.text_color ||
    draft.button_color !== saved.button_color;

  // The in-page "Tienes cambios sin guardar" bar only helps if the user
  // stays on the page to see it — closing the tab or hitting refresh with
  // unsaved changes previously lost them with no warning at all.
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const generatedPalette = useMemo(
    () => generateColorPalette(draft.button_color),
    [draft.button_color]
  );

  const previewColors = useMemo(() => {
    const palette = generateColorPalette(draft.button_color);
    return {
      background: draft.background_color,
      text: draft.text_color,
      button: draft.button_color,
      buttonAlt: palette?.secondary || draft.button_color,
      buttonText: palette?.textOnPrimary || getReadableTextColor(draft.button_color),
    };
  }, [draft]);

  const handleTemplateChange = (template) => {
    if (heredaMarca) return; // locked while this profile inherits the company brand
    setDraft((prev) => ({ ...prev, template }));
  };

  const handleSelectTheme = (key) => {
    if (heredaMarca) return; // locked while this profile inherits the company brand
    if (key === 'custom') {
      setDraft((prev) => ({ ...prev, theme: 'custom' }));
      return;
    }
    const theme = COLOR_THEMES[key];
    if (!theme) return;
    setDraft((prev) => ({
      ...prev,
      theme: key,
      background_color: theme.background,
      text_color: theme.text,
      button_color: theme.button,
    }));
  };

  const handleApplySuggestedColors = () => {
    if (!generatedPalette) return;
    setDraft((prev) => ({
      ...prev,
      background_color: generatedPalette.backgroundLight,
      text_color: generatedPalette.textOnLight,
    }));
  };

  const handleDiscard = () => setDraft(saved);

  const handleSaveAll = async () => {
    setSaving(true);

    const palette = generateColorPalette(draft.button_color);
    const result = await updateProfile({
      template: draft.template,
      theme_color: draft.theme,
      background_color: draft.background_color,
      text_color: draft.text_color,
      button_color: draft.button_color,
      button_alt_color: palette?.secondary || draft.button_color,
      button_text_color: palette?.textOnPrimary || getReadableTextColor(draft.button_color),
    });

    if (result.success) {
      setSaved(draft);
      setMessage(t('appearance.toast.saved', 'Cambios guardados correctamente'));
    } else {
      setMessage(`Error: ${result.error}`);
    }

    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  if (!profile) {
    return (
      <div className="w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">{t('appearance.selectProfile', 'Selecciona un perfil para personalizar')}</p>
        </div>
      </div>
    );
  }

  const messageType = message.startsWith('Error') ? 'error' : 'ok';
  const showContrastWarning =
    draft.theme === 'custom' &&
    (draft.template === 'corporate' || draft.template === 'portrait') &&
    !hasGoodContrast(draft.text_color, draft.background_color);

  return (
    <div className="w-full dashboard-container">
      <style>{AE_CSS}</style>
      <div className="mb-8">
        <h1 className="ae-title">{t('appearance.title', 'Personalizar Apariencia')}</h1>
        <p className="ae-subtitle">
          {t('appearance.editing', 'Personalizando')}: {profile.name} {profile.lastname}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 ae-mobile-stack">
        {/* Panel izquierdo - Controles */}
        <div className="space-y-8 ae-mobile-controls">
          {message && (
            <div className={`ae-banner ae-banner--${messageType}`}>
              {message}
            </div>
          )}

          <div className={`ae-savebar ${isDirty ? 'is-dirty' : ''}`}>
            <span className="ae-savebar-label">
              {isDirty ? t('appearance.unsaved', 'Tienes cambios sin guardar') : t('appearance.allSaved', 'Todo guardado')}
            </span>
            <div className="flex gap-2">
              {isDirty && (
                <button className="ae-discard-btn" onClick={handleDiscard} disabled={saving}>
                  {t('appearance.discard', 'Descartar')}
                </button>
              )}
              <button
                className="ae-save-btn ae-save-btn--bar"
                onClick={handleSaveAll}
                disabled={!isDirty || saving}
              >
                {saving ? t('appearance.saving', 'Guardando...') : t('appearance.saveChanges', 'Guardar cambios')}
              </button>
            </div>
          </div>

          {/* Brand-kit inheritance state (Enterprise only) */}
          {heredaMarca ? (
            <div className="ae-brand-banner">
              <span className="ae-brand-banner-text">
                <strong>{t('appearance.brand.usesCompanyBrand', 'Este perfil usa la marca de tu empresa.')}</strong> {t('appearance.brand.usesCompanyBrandSub', 'La plantilla y el color de abajo vienen de ahí.')}
              </span>
              <button className="ae-brand-banner-btn" onClick={handleCustomizeThisProfile} disabled={applyingBrand}>
                {applyingBrand ? t('appearance.brand.applying', 'Aplicando...') : t('appearance.brand.customizeThisProfile', 'Personalizar solo este perfil')}
              </button>
            </div>
          ) : canUseBrandKit && brandKitExists ? (
            <div className="ae-brand-banner">
              <span className="ae-brand-banner-text">{t('appearance.brand.doesNotUse', 'Este perfil no usa la marca de tu empresa.')}</span>
              <button className="ae-brand-banner-btn" onClick={handleUseCompanyBrand} disabled={applyingBrand}>
                {applyingBrand ? t('appearance.brand.applying', 'Aplicando...') : t('appearance.brand.useCompanyBrand', 'Usar la marca de mi empresa')}
              </button>
            </div>
          ) : canUseBrandKit ? (
            <div className="ae-brand-banner">
              <span className="ae-brand-banner-text">
                {t('appearance.brand.promo', '¿Quieres el mismo estilo en todos tus perfiles? Configura la marca de tu empresa una sola vez y se aplicará automáticamente a los perfiles nuevos.')}
              </span>
              <button className="ae-brand-banner-btn" onClick={() => setActiveTab && setActiveTab('brand_kit')}>
                {t('appearance.brand.goToBrand', 'Ir a Marca')}
              </button>
            </div>
          ) : null}

          {/* Plantilla */}
          <div className={`ae-section ${heredaMarca ? 'ae-locked' : ''}`} data-locked-tip={heredaMarca ? t('appearance.lockedByBrand', 'Definido por la marca de tu empresa') : undefined}>
            <h3 className="ae-sec-title">{t('appearance.template.title', 'Plantilla')}</h3>
            <div className="ae-tpl-grid">
              <button
                onClick={() => handleTemplateChange('classic')}
                className={`ae-tpl-btn ${draft.template === 'classic' ? 'on' : ''}`}
                disabled={heredaMarca}
              >
                <span className="ae-tpl-name">{t('appearance.template.classic', 'Clásica')}</span>
                <span className="ae-tpl-desc">{t('appearance.template.classicDesc', 'Fondo inmersivo con degradado de tu color principal')}</span>
              </button>

              <button
                onClick={() => handleTemplateChange('corporate')}
                className={`ae-tpl-btn ${draft.template === 'corporate' ? 'on' : ''}`}
                disabled={heredaMarca}
              >
                <span className="ae-tpl-name">{t('appearance.template.corporate', 'Corporativa')}</span>
                <span className="ae-tpl-desc">{t('appearance.template.corporateDesc', 'Fondo sólido y banner, con colores de fondo y texto propios')}</span>
              </button>

              <button
                onClick={() => handleTemplateChange('portrait')}
                className={`ae-tpl-btn ${draft.template === 'portrait' ? 'on' : ''}`}
                disabled={heredaMarca}
              >
                <span className="ae-tpl-name">{t('appearance.template.portrait', 'Retrato')}</span>
                <span className="ae-tpl-desc">{t('appearance.template.portraitDesc', 'Tu foto a página completa, con un panel de color debajo')}</span>
              </button>
            </div>
          </div>

          {/* Marca de agua */}
          <div className="ae-section">
            <h3 className="ae-sec-title">{t('appearance.watermark.title', 'Marca de agua')}</h3>
            {canRemoveWatermark ? (
              <div className="ae-wm-row">
                <div className="ae-wm-check">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="ae-wm-active-title">{t('appearance.watermark.disabledTitle', 'Marca de agua de Contact desactivada')}</div>
                  <div className="ae-wm-active-sub">{t('appearance.watermark.disabledSub', 'Tu perfil público no muestra el logo de Contact.')}</div>
                </div>
              </div>
            ) : (
              <div data-locked-tip={t('appearance.watermark.requiresPaid', 'Requiere un plan de pago')}>
                <button
                  type="button"
                  className="ae-wm-btn ae-locked"
                  onClick={() => openUpgrade('remove_watermark')}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V8a4 4 0 018 0v3" strokeLinecap="round" />
                  </svg>
                  <span>{t('appearance.watermark.removeButton', 'Quitar marca de agua Contact')}</span>
                </button>
                <p className="ae-wm-hint">{t('appearance.watermark.upgradeHint', 'Actualiza a un plan de pago para quitar el logo de Contact de tu perfil público.')}</p>
              </div>
            )}
          </div>

          {/* Color: temas predefinidos + personalizado, unificados */}
          <div className={`ae-section ${heredaMarca ? 'ae-locked' : ''}`} data-locked-tip={heredaMarca ? t('appearance.lockedByBrand', 'Definido por la marca de tu empresa') : undefined}>
            <h3 className="ae-sec-title">{t('appearance.color.title', 'Color')}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ae-mobile-theme-grid">
              {Object.entries(COLOR_THEMES).map(([key, theme]) => (
                <div
                  key={key}
                  onClick={() => handleSelectTheme(key)}
                  className={`ae-theme-card ${draft.theme === key ? 'on' : ''}`}
                  style={{ backgroundColor: theme.background }}
                >
                  <div className="text-center">
                    <div
                      className="ae-color-swatch-lg w-12 h-12 rounded-full mx-auto mb-3"
                      style={{ backgroundColor: theme.button }}
                    ></div>
                    <h4 className="font-medium" style={{ color: theme.text }}>
                      {themeName(key, theme, t)}
                    </h4>
                    <div className="flex justify-center space-x-1 mt-2">
                      <div className="ae-color-swatch-sm w-3 h-3 rounded-full" style={{ backgroundColor: theme.background }}></div>
                      <div className="ae-color-swatch-sm w-3 h-3 rounded-full" style={{ backgroundColor: theme.text }}></div>
                      <div className="ae-color-swatch-sm w-3 h-3 rounded-full" style={{ backgroundColor: theme.button }}></div>
                    </div>
                  </div>
                </div>
              ))}

              <div
                onClick={() => handleSelectTheme('custom')}
                className={`ae-theme-card ae-theme-card--custom ${draft.theme === 'custom' ? 'on' : ''}`}
              >
                <div className="text-center">
                  <div
                    className="ae-color-swatch-lg w-12 h-12 rounded-full mx-auto mb-3"
                    style={{ background: 'conic-gradient(from 0deg, #3b82f6, #10b981, #f59e0b, #ef4444, #8b5cf6, #3b82f6)' }}
                  ></div>
                  <h4 className="font-medium" style={{ color: '#0A0A0A' }}>{t('appearance.color.custom', 'Personalizado')}</h4>
                  <p style={{ fontSize: 11, color: '#9A9A9A', marginTop: 6, fontFamily: "'Inter',system-ui,sans-serif" }}>
                    {t('appearance.color.customDesc', 'Elige tus propios colores')}
                  </p>
                </div>
              </div>
            </div>

            {draft.theme === 'custom' && (
              <div className="ae-custom-colors">
                <div className="grid grid-cols-1 gap-6">
                  {/* Color Principal (genera paleta automáticamente) */}
                  <div>
                    <label className="ae-form-label">{t('appearance.color.mainButtonColor', 'Color Principal de Botones')}</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={draft.button_color}
                        onChange={(e) => setDraft((prev) => ({ ...prev, button_color: e.target.value }))}
                        disabled={heredaMarca}
                        className="ae-color-input"
                      />
                      <input
                        type="text"
                        value={draft.button_color}
                        onChange={(e) => setDraft((prev) => ({ ...prev, button_color: e.target.value }))}
                        disabled={heredaMarca}
                        className="ae-text-input"
                        placeholder="#7CDAF9"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('appearance.color.mainButtonColorHint', 'Este color genera automáticamente el resto de la paleta')}</p>
                  </div>

                  {/* Paleta generada automáticamente */}
                  {generatedPalette && (
                    <div className="ae-palette-box">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-700">✨ {t('appearance.color.paletteTitle', 'Paleta Generada Automáticamente')}</h4>
                        <button onClick={handleApplySuggestedColors} className="ae-apply-btn" disabled={heredaMarca}>
                          {t('appearance.color.applySuggestions', 'Aplicar sugerencias')}
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <div className="ae-palette-swatch w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.primary }}></div>
                          <p className="text-xs text-gray-600">{t('appearance.color.primary', 'Principal')}</p>
                        </div>
                        <div className="text-center">
                          <div className="ae-palette-swatch w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.secondary }}></div>
                          <p className="text-xs text-gray-600">{t('appearance.color.secondary', 'Secundario')}</p>
                        </div>
                        <div className="text-center">
                          <div className="ae-palette-swatch w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.hover }}></div>
                          <p className="text-xs text-gray-600">Hover</p>
                        </div>
                        <div className="text-center">
                          <div className="ae-palette-swatch w-full h-12 rounded-lg border border-gray-200 mb-1" style={{ backgroundColor: generatedPalette.accent }}></div>
                          <p className="text-xs text-gray-600">{t('appearance.color.accent', 'Acento')}</p>
                        </div>
                      </div>
                      {generatedPalette.contrasts && (
                        <div className="mt-3 text-xs text-gray-600">
                          <p>✓ {t('appearance.color.textContrast', 'Contraste sobre texto')}: {generatedPalette.contrasts.textOnPrimaryContrast.toFixed(2)}:1
                            {generatedPalette.contrasts.textOnPrimaryContrast >= 4.5 ? ' ✅' : ` ⚠️ ${t('appearance.color.lowContrast', 'Bajo contraste')}`}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {draft.template === 'corporate' || draft.template === 'portrait' ? (
                    <>
                      {/* Color de Fondo */}
                      <div>
                        <label className="ae-form-label">{t('appearance.color.backgroundColor', 'Color de Fondo')}</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={draft.background_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, background_color: e.target.value }))}
                            disabled={heredaMarca}
                            className="ae-color-input"
                          />
                          <input
                            type="text"
                            value={draft.background_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, background_color: e.target.value }))}
                            disabled={heredaMarca}
                            className="ae-text-input"
                          />
                        </div>
                      </div>

                      {/* Color de Texto */}
                      <div>
                        <label className="ae-form-label">{t('appearance.color.textColor', 'Color de Texto')}</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={draft.text_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, text_color: e.target.value }))}
                            disabled={heredaMarca}
                            className="ae-color-input"
                          />
                          <input
                            type="text"
                            value={draft.text_color}
                            onChange={(e) => setDraft((prev) => ({ ...prev, text_color: e.target.value }))}
                            disabled={heredaMarca}
                            className="ae-text-input"
                          />
                        </div>
                        {showContrastWarning && (
                          <p style={{ fontSize: 11.5, color: '#8a6d00', marginTop: 6, fontFamily: "'Inter',system-ui,sans-serif" }}>
                            ⚠️ {t('appearance.color.contrastWarning', 'El contraste entre texto y fondo puede no ser legible')}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="ae-tpl-note">
                      {t('appearance.color.classicNote', 'La plantilla Clásica usa un degradado automático a partir de tu color principal — el fondo y el texto no son personalizables en este modo. Cambia a Corporativa o Retrato para elegirlos manualmente.')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Panel derecho - Vista previa en tiempo real */}
        <div className="lg:sticky lg:top-8 lg:self-start ae-mobile-preview-first">
          <div className="ae-section">
            <h3 className="ae-sec-title">{t('appearance.preview.title', 'Vista Previa')}</h3>
            <div className="flex justify-center">
              <div className="ae-preview-frame rounded-2xl border border-gray-200 overflow-hidden" style={{ width: 375 }}>
                <ProfilePreview
                  profile={profile}
                  template={draft.template}
                  colors={previewColors}
                  isPreview={true}
                  showWatermark={!canRemoveWatermark}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-3">
              {isDirty ? t('appearance.preview.unsaved', 'Cambios sin guardar — así se vería tu perfil') : t('appearance.preview.current', 'Así se ve tu perfil actualmente')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceEditor;
